import { Nullable } from '@kts-front/types';
import { z } from 'zod';

export enum FixationFields {
  subAgent = 'sub_agent',
  projectId = 'project_id',
  placeTypeId = 'place_type_id',
  comment = 'comment',
}

export enum MeetingFields {
  projectId = 'project_id',
  slotId = 'slot_id',
  leadId = 'lead_id',
  subAgent = 'sub_agent',
}

export enum ClientEditFieldsKey {
  phone = 'phone',
  firstName = 'first_name',
  lastName = 'last_name',
  patronymic = 'patronymic',
  email = 'email',
}

export enum SubAgentEditFieldsKey {
  phone = 'phone',
  firstName = 'first_name',
  lastName = 'last_name',
  patronymic = 'patronymic',
  email = 'email',
}

export enum EmployeeEditFieldsKey {
  firstName = 'first_name',
  lastName = 'last_name',
  patronymic = 'patronymic',
  phone = 'phone',
  email = 'email',
  role = 'role',
  blocked = 'blocked',
  password = 'password',
  passwordConfirm = 'password_confirm',
}

export enum CompanyFieldsKey {
  type = 'type',
  inn = 'inn',
  legalName = 'legal_name',
  legalAddress = 'legal_address',
  ogrn = 'ogrn',
  kpp = 'kpp',
  marketingName = 'marketing_name',
}

export enum CompanyTypeKey {
  ip = 'ip',
  ooo = 'ul',
}

export enum CheckUniqueClientMethod {
  phoneOnly = 'phoneOnly',
  phoneAndProject = 'phoneAndProject',
}

export enum ShowContentEditorFieldsKey {
  projects = 'projects',
  documents = 'documents',
  about = 'about',
}

export enum FixationMode {
  OnlyAfterMeeting = 'onlyAfterMeeting',
  SingleWindow = 'singleWindow',
}

/** Общие enum'ы для списков полей */
const fixationFieldEnum = z.nativeEnum(FixationFields);
const meetingFieldEnum = z.nativeEnum(MeetingFields);
const clientCreationFieldEnum = z.nativeEnum(ClientEditFieldsKey);
const subAgentCreationFieldEnum = z.nativeEnum(SubAgentEditFieldsKey);
const employeeCreationFieldEnum = z.nativeEnum(EmployeeEditFieldsKey);
const checkUniqueClientEnum = z.nativeEnum(CheckUniqueClientMethod);
const companyFieldsValues = new Set<string>(Object.values(CompanyFieldsKey));
const parseCompanyCreationFields = (fields: string[]): CompanyFieldsKey[] =>
  fields.filter((field): field is CompanyFieldsKey => companyFieldsValues.has(field));

/** colors[i] = { type: "string", value: string[] } */
const colorItemSchema = z.object({
  type: z.string(),
  // 10 значений по требованиям UI библиотеки
  value: z.tuple([
    z.string(),
    z.string(),
    z.string(),
    z.string(),
    z.string(),
    z.string(),
    z.string(),
    z.string(),
    z.string(),
    z.string(),
  ]),
});

/** Блок UI */
const uiSchema = z.object({
  colors: z.array(colorItemSchema),
  primary_color: z.string(),
  primary_shade: z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
    z.literal(7),
    z.literal(8),
    z.literal(9),
  ]),
  /** Сколько чипов типа документа показывать на странице «Документы», если в конфиге не задано иное */
  documents_type_filter_max_visible_chips: z.number().int().positive().optional(),
  /** Соотношение сторон (ширина / высота) для изображения в Hero страницы проекта */
  project_hero_image_aspect_ratio: z.number().positive().optional(),
  /** Соотношение сторон (ширина / высота) для изображения карточки проекта в списках */
  project_card_image_aspect_ratio: z.number().positive().optional(),
});

const mediaSchema = z.object({
  id: z.number(),
  filename: z.string(),
  mime_type: z.string().nullable(),
  url: z.string(),
});

/** Блок Images */
const imagesSchema = z.object({
  logo: mediaSchema,
  logo_width: z.number().nullable(),
  mobile_logo: mediaSchema,
  start_image: mediaSchema,
  start_image_tablet: mediaSchema,
  start_image_mobile: mediaSchema,
  start_image_webp: mediaSchema,
  start_image_tablet_webp: mediaSchema,
  start_image_mobile_webp: mediaSchema,
});

/** Блок Logic.Booking */
const bookingSchema = z.object({
  show: z.boolean().optional(),
  enabled: z.boolean(),
  use_readiness_date: z.boolean().optional(),
  show_reservation_status: z.boolean().optional(),
});

/** Блок Logic.Fixation */
const fixationSchema = z.object({
  show: z.boolean().optional(),
  enabled: z.boolean().optional(),
  shown_fields: z.array(fixationFieldEnum),
  optional_fields: z.array(fixationFieldEnum),
  mode: z.nativeEnum(FixationMode).optional(),
});

/** Блок Logic.Meeting */
const meetingSchema = z.object({
  show: z.boolean().optional(),
  enabled: z.boolean().optional(),
  shown_fields: z.array(meetingFieldEnum),
  optional_fields: z.array(meetingFieldEnum),
  update_shown_fields: z.array(meetingFieldEnum).optional(),
  update_optional_fields: z.array(meetingFieldEnum).optional(),
  show_cancel_meeting_button: z.boolean().optional(),
  show_update_meeting_button: z.boolean().optional(),
  show_status: z.boolean().optional(),
  check_unique_client: checkUniqueClientEnum.optional(),
  can_edit_project: z.boolean().optional(),
  can_edit_sub_agent: z.boolean().optional(),
  filter_by_unique_clients_and_leads: z.boolean().optional(),
});

/** Блок Logic.ClientCreation */
const clientCreationSchema = z.object({
  shown_fields: z.array(clientCreationFieldEnum),
  optional_fields: z.array(clientCreationFieldEnum),
});

/** Блок Logic.SubAgentCreation */
const subAgentCreationSchema = z.object({
  shown_fields: z.array(subAgentCreationFieldEnum),
  optional_fields: z.array(subAgentCreationFieldEnum),
});

/** Блок Logic.EmployeeCreation */
const employeeCreationSchema = z.object({
  shown_fields: z.array(employeeCreationFieldEnum),
  optional_fields: z.array(employeeCreationFieldEnum),
});

/** Блок Logic.CompanyCreation */
const companyCreationSchema = z.object({
  shown_fields: z.array(z.string()).transform(parseCompanyCreationFields),
  optional_fields: z.array(z.string()).transform(parseCompanyCreationFields),
});

/** Блок Logic.PlaceType */
const placeTypeSchema = z.object({
  show: z.boolean().optional(),
});

export type Link = {
  title: string;
  url: string;
  download?: boolean;
};

const registrationSchema = z
  .object({
    /** Почта поддержки на экране успешной регистрации */
    support_email: z.string().email().optional(),
  })
  .optional();

const showContentEditorFieldEnum = z.nativeEnum(ShowContentEditorFieldsKey);

export const DEFAULT_LOGO_WIDTH = 128;

export const DEFAULT_DOCUMENT_TYPE_FILTER_MAX_VISIBLE_CHIPS = 3;

export const DEFAULT_PROJECT_HERO_IMAGE_ASPECT_RATIO = 1.77;

export const DEFAULT_PROJECT_CARD_IMAGE_ASPECT_RATIO = 1.77;

/** Весь объект */
export const appConfigSchema = z
  .object({
    ui: uiSchema,
    images: imagesSchema,
    logic: z.object({
      booking: bookingSchema,
      fixation: fixationSchema,
      meeting: meetingSchema,
      placeType: placeTypeSchema,
      client_creation: clientCreationSchema,
      sub_agent_creation: subAgentCreationSchema.optional(),
      employee_creation: employeeCreationSchema,
      company_creation: companyCreationSchema,
      registration: registrationSchema,
      show_content_editor: z.array(showContentEditorFieldEnum).optional(),
      check_unique_phone: z.boolean().optional(),
      /** Названия разделов ЛК (меню и заголовки страниц). Ключи: employees, clients, leads, projects, meetings, about, lots, documents */
      section_titles: z.record(z.string(), z.string()).optional(),
    }),
  })
  .transform((raw) => ({
    ui: {
      colors: raw.ui.colors,
      primaryColor: raw.ui.primary_color,
      primaryShade: raw.ui.primary_shade,
      documentsTypeFilterMaxVisibleChips:
        raw.ui.documents_type_filter_max_visible_chips ?? DEFAULT_DOCUMENT_TYPE_FILTER_MAX_VISIBLE_CHIPS,
      projectHeroImageAspectRatio: raw.ui.project_hero_image_aspect_ratio ?? DEFAULT_PROJECT_HERO_IMAGE_ASPECT_RATIO,
      projectCardImageAspectRatio: raw.ui.project_card_image_aspect_ratio ?? DEFAULT_PROJECT_CARD_IMAGE_ASPECT_RATIO,
    },
    images: {
      startImageUrl: raw.images.start_image.url,
      startImageUrlTablet: raw.images.start_image_tablet.url,
      logoWidth: raw.images.logo_width ?? DEFAULT_LOGO_WIDTH,
      startImageUrlMobile: raw.images.start_image_mobile.url,
      startImageUrlWebp: raw.images.start_image_webp.url,
      startImageUrlTabletWebp: raw.images.start_image_tablet_webp.url,
      startImageUrlMobileWebp: raw.images.start_image_mobile_webp.url,
      logoUrl: raw.images.logo.url,
      mobileLogoUrl: raw.images.mobile_logo.url,
    },
    logic: {
      booking: {
        show: raw.logic.booking.show ?? true,
        enabled: raw.logic.booking.enabled ?? true,
        useReadinessDate: raw.logic.booking.use_readiness_date ?? false,
        showReservationStatus: raw.logic.booking.show_reservation_status ?? true,
      },
      fixation: {
        mode: raw.logic.fixation.mode || FixationMode.SingleWindow,
        show: raw.logic.fixation.show ?? true,
        enabled: raw.logic.fixation.enabled ?? true,
        shownFields: new Set(raw.logic.fixation.shown_fields ?? []),
        optionalFields: new Set(raw.logic.fixation.optional_fields ?? []),
      },
      meeting: {
        show: raw.logic.meeting.show ?? true,
        enabled: raw.logic.meeting.enabled ?? true,
        shownFields: new Set(raw.logic.meeting.shown_fields ?? []),
        optionalFields: new Set(raw.logic.meeting.optional_fields ?? []),
        updateShownFields: new Set(raw.logic.meeting.update_shown_fields ?? []),
        updateOptionalFields: new Set(raw.logic.meeting.update_optional_fields ?? []),
        showCancelMeetingButton: raw.logic.meeting.show_cancel_meeting_button ?? true,
        showUpdateMeetingButton: raw.logic.meeting.show_update_meeting_button ?? true,
        showStatus: raw.logic.meeting.show_status ?? true,
        checkUniqueClient: raw.logic.meeting.check_unique_client ?? CheckUniqueClientMethod.phoneAndProject,
        canEditProject: raw.logic.meeting.can_edit_project ?? true,
        canEditSubAgent: raw.logic.meeting.can_edit_sub_agent ?? true,
        filterByUniqueClientsAndLeads: raw.logic.meeting.filter_by_unique_clients_and_leads ?? true,
      },
      clientCreation: {
        shownFields: new Set(raw.logic.client_creation.shown_fields ?? []),
        optionalFields: new Set(raw.logic.client_creation.optional_fields ?? []),
      },
      subAgentCreation: {
        shownFields: new Set(raw.logic.sub_agent_creation?.shown_fields ?? []),
        optionalFields: new Set(raw.logic.sub_agent_creation?.optional_fields ?? []),
      },
      employeeCreation: {
        shownFields: new Set(raw.logic.employee_creation.shown_fields ?? []),
        optionalFields: new Set(raw.logic.employee_creation.optional_fields ?? []),
      },
      companyCreation: {
        shownFields: new Set(raw.logic.company_creation.shown_fields ?? []),
        optionalFields: new Set(raw.logic.company_creation.optional_fields ?? []),
      },
      placeType: {
        show: raw.logic.placeType.show ?? true,
      },
      registration: {
        supportEmail: raw.logic.registration?.support_email,
      },
      showContentEditor: raw.logic.show_content_editor ?? [],
      checkUniquePhone: raw.logic.check_unique_phone ?? false,
      sectionTitles: raw.logic.section_titles ?? {},
    },
  }));

export type AppConfig = z.infer<typeof appConfigSchema>;

export type ColorToken = z.infer<typeof colorItemSchema>;

export type UIConfig = AppConfig['ui'];

export type Images = AppConfig['images'];

export type LogicConfig = {
  booking: AppConfig['logic']['booking'];
  fixation: AppConfig['logic']['fixation'];
  meeting: AppConfig['logic']['meeting'];
  clientCreation: AppConfig['logic']['clientCreation'];
  subAgentCreation: AppConfig['logic']['subAgentCreation'];
  employeeCreation: AppConfig['logic']['employeeCreation'];
  companyCreation: AppConfig['logic']['companyCreation'];
  placeType: AppConfig['logic']['placeType'];
  registration: AppConfig['logic']['registration'];
  showContentEditor: AppConfig['logic']['showContentEditor'];
  checkUniquePhone: AppConfig['logic']['checkUniquePhone'];
  sectionTitles: AppConfig['logic']['sectionTitles'];
};

export interface IAppConfigBootstrap {
  isLoading: boolean;
  error: Nullable<string>;
  isReady: boolean;
  bootstrapUi: UIConfig;
}

export interface IAppConfigReady {
  config: AppConfig;
  ui: UIConfig;
  images: Images;
  logic: LogicConfig;
}

export type AppConfigParsingError = {
  path: string;
  message: string;
};

export type AppConfigFetchErrorData = {
  parsingErrors: AppConfigParsingError[];
  httpStatusCode?: number;
};
